<template>
    <div class="disease-research" v-loading="loading">
        <p v-if="diseaseName" class="disease-name">当前疾病：{{ diseaseName }}</p>
        <section
            v-for="item in sections"
            :id="item.id"
            :key="item.key"
            class="research-section"
        >
            <div class="section-head">
                <h3 class="section-tit">{{ item.title }}</h3>
            </div>
            <div class="section-body">
                <template v-if="item.key === 'literature'">
                    <research-literature :all-list="literatureList" />
                    <research-graph-block
                        graph-title="科研文献支撑图谱"
                        table-title="科研文献支撑三元组表格"
                        :chart-data="graphs[item.key]"
                    />
                </template>
                <clinical-trial v-else-if="item.key === 'clinical'" :chart-data="graphs[item.key]" />
                <basic-experiment v-else-if="item.key === 'basic'" :chart-data="graphs[item.key]" />
                <case-report v-else-if="item.key === 'case'" :chart-data="graphs[item.key]" />
                <guideline-consensus v-else-if="item.key === 'guideline'" :chart-data="graphs[item.key]" />
                <doctor-experience v-else-if="item.key === 'experience'" :chart-data="graphs[item.key]" />
                <review-commentary v-else-if="item.key === 'review'" :chart-data="graphs[item.key]" />
            </div>
        </section>
    </div>
</template>

<script>
import { createGraphRequestState } from './graphRequestState';
import ResearchLiterature from './comps/research_literature.vue';
import ClinicalTrial from './comps/clinical_trial.vue';
import BasicExperiment from './comps/basic_experiment.vue';
import CaseReport from './comps/case_report.vue';
import GuidelineConsensus from './comps/guideline_consensus.vue';
import DoctorExperience from './comps/doctor_experience.vue';
import ReviewCommentary from './comps/review_commentary.vue';
import ResearchGraphBlock from './comps/research_graph_block.vue';
import { getResearchGraph, getResearchDiseaseName, getResearchLiterature } from './researchData';
import { getStudentInfo } from '../../io/api.js';

export default {
    name: 'DiseaseResearch',
    components: {
        ResearchLiterature,
        ClinicalTrial,
        BasicExperiment,
        CaseReport,
        GuidelineConsensus,
        DoctorExperience,
        ReviewCommentary,
        ResearchGraphBlock,
    },
    provide() {
        return { diseaseGraphRequest: createGraphRequestState(this, 'loadResearchData') };
    },
    props: {
        researchData: {
            type: Object,
            // 可由父组件传入业务响应的 data；未传入时走统一请求入口。
            default: null,
        },
    },
    data() {
        return {
            loading: false,
            loadError: '',
            responseData: {},
            requestId: 0,
            sections: [
                // 图谱、三元组列表分别对应接口返回的两套字段。
                { key: 'literature', id: 'researchLiterature', title: '科研文献', graphField: 'researchLiteratureSupportGraph', tripleField: 'researchLiteratureSupportTriple' },
                { key: 'clinical', id: 'clinicalTrial', title: '临床试验', graphField: 'clinicalTrialGraph', tripleField: 'clinicalTrialTriple' },
                { key: 'basic', id: 'basicExperiment', title: '基础实验', graphField: 'basicExperimentGraph', tripleField: 'basicExperimentTriple' },
                { key: 'case', id: 'caseReport', title: '个案报道', graphField: 'caseReportGraph', tripleField: 'caseReportTriple' },
                { key: 'guideline', id: 'guidelineConsensus', title: '指南与共识', graphField: 'guidelineConsensusGraph', tripleField: 'guidelineConsensusTriple' },
                { key: 'experience', id: 'doctorExperience', title: '名医经验', graphField: 'famousDoctorExperienceGraph', tripleField: 'famousDoctorExperienceTriple' },
                { key: 'review', id: 'reviewCommentary', title: '述评与综述', graphField: 'reviewCommentaryGraph', tripleField: 'reviewCommentaryTriple' },
            ],
        };
    },
    computed: {
        currentData() {
            return this.researchData || this.responseData;
        },
        graphs() {
            return this.sections.reduce((graphs, item) => {
                graphs[item.key] = getResearchGraph(this.currentData, item.graphField, item.tripleField);
                return graphs;
            }, {});
        },
        diseaseName() {
            return getResearchDiseaseName(this.currentData);
        },
        literatureList() {
            return getResearchLiterature(this.currentData);
        },
    },
    watch: {
        researchData: {
            immediate: true,
            handler(data) {
                if (data) {
                    // 外部数据接管展示后，忽略此前尚未完成的请求。
                    this.requestId += 1;
                    this.loading = false;
                    this.loadError = '';
                } else {
                    this.loadResearchData();
                }
            },
        },
        '$route.query.word'() {
            if (!this.researchData) this.loadResearchData();
        },
    },
    beforeDestroy() {
        this.requestId += 1;
    },
    methods: {
        async loadResearchData() {
            const requestId = ++this.requestId;
            this.loading = true;
            this.loadError = '';
            this.responseData = {};
            try {
                // 请求位置：后续在 src/io/api.js 的 getStudentInfo 中替换真实请求。
                const response = await getStudentInfo({ diseaseName: this.$route.query.word || '' });
                const { code, data, msg } = response.data || {};
                // 快速切换疾病或离开模块时，不让旧请求覆盖当前内容。
                if (requestId !== this.requestId) return;
                if (code !== 0 && code !== '0') {
                    this.loadError = msg || '科研数据加载失败';
                    return;
                }
                this.responseData = data || {};
            } catch (error) {
                if (requestId !== this.requestId) return;
                this.loadError = error.message || '科研数据加载失败，请稍后重试';
            } finally {
                if (requestId === this.requestId) this.loading = false;
            }
        },
    },
};
</script>

<style lang="less" scoped>
@import './index.less';

.disease-name {
    margin: 0 0 16px;
    color: #0C55AA;
    font-weight: bold;
}

.research-literature {
    margin-bottom: 20px;
}
</style>
